import {
    isValidPaymentSelection,
    type CollectionRouting,
    type DealerCollectedDeposit,
    type PaymentSelection,
} from '../types/schedule.types';
import { isDateKey } from '../utils/dateKey';


// #475-B / #62 contract: the wire shape for a confirmed payment-schedule
// selection. `method_key` + `params` + `snapshot` round-trip losslessly:
// `params` is required because the restore guard (`isValidPaymentSelection`)
// rejects a value whose `params` is not a non-null object (D6) — omitting it
// here would make persistence a silent no-op for resume. quote-service (#62)
// persists this verbatim in the `payment_selection` json column and returns
// it on read.
export interface PaymentSelectionPayload {
    method_key: string;
    params: PaymentSelection['params'];
    snapshot: PaymentSelection['snapshot'];
    // #728: the collection lead (whole months) that produced `params`. Omitted
    // entirely (not sent as `null`) when the in-app selection carries none —
    // see toPaymentSelectionPayload below — so a legacy or non-item-based
    // selection still produces a byte-identical wire payload.
    collection_lead_months?: number;
    /**
     * #767: `YYYY-MM-DD`. Omitted entirely (not `null`) when the in-app
     * selection carries none, so a pre-#767 selection produces a
     * byte-identical wire payload — see toPaymentSelectionPayload below.
     */
    schedule_anchor_date?: string;
    /** Omitted entirely (never sent as `null`) whenever the selection carries
     *  neither axis, so a quote with no routing at all is unchanged. */
    collection_routing?: {
        deposit_collection?: 'dealer';
        /** UNLIKE deposit_collection, `elemis` IS persisted here: absence means
         *  the legacy born-Active outcome, so case 2 needs the explicit value. */
        full_payment_collection?: 'elemis' | 'dealer';
        dealer_payment?: {
            amount_minor: number;
            reference: string;
            invoice_number?: string;
            collected_at: string;
        };
    };
}

const isValidDealerPayment = (value: unknown): value is DealerCollectedDeposit => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as Partial<DealerCollectedDeposit>;
    return (
        typeof candidate.amountMinor === 'number' &&
        Number.isInteger(candidate.amountMinor) &&
        candidate.amountMinor > 0 &&
        typeof candidate.reference === 'string' &&
        candidate.reference.trim().length > 0 &&
        isDateKey(candidate.collectedAt) &&
        (candidate.invoiceNumber === undefined || typeof candidate.invoiceNumber === 'string')
    );
};

const toDealerPaymentPayload = (
    dealerPayment: DealerCollectedDeposit,
): NonNullable<PaymentSelectionPayload['collection_routing']>['dealer_payment'] => {
    const { amountMinor, reference, invoiceNumber, collectedAt } = dealerPayment;
    return {
        amount_minor: amountMinor,
        reference,
        ...(invoiceNumber ? { invoice_number: invoiceNumber } : {}),
        collected_at: collectedAt,
    };
};

const PAID_IN_FULL_METHOD_KEY = 'paid_in_full';

// An absent full-payment axis is the legacy born-Active outcome, so a present routing
// this mapping cannot express must never reach the wire as "nobody collected".
const MALFORMED_FULL_PAYMENT_ROUTING =
    'Malformed collection routing on the quote wire: full_payment_collection';

// The deposit axis omits an `elemis` choice; the full-payment axis persists it, because
// absence there is the legacy outcome rather than "Elemis collects".
const toCollectionRoutingPayload = (
    routing: CollectionRouting | undefined,
    methodKey: string,
): PaymentSelectionPayload['collection_routing'] => {
    if (routing === undefined) {
        return undefined;
    }
    const isPaidInFull = methodKey === PAID_IN_FULL_METHOD_KEY;
    const isDealerDeposit =
        !isPaidInFull &&
        routing.depositCollection === 'dealer' &&
        isValidDealerPayment(routing.dealerPayment);
    const isDealerFullPayment =
        isPaidInFull &&
        routing.fullPaymentCollection === 'dealer' &&
        isValidDealerPayment(routing.dealerPayment);
    if (isDealerDeposit || isDealerFullPayment) {
        return {
            ...(isDealerDeposit ? { deposit_collection: 'dealer' as const } : {}),
            ...(isDealerFullPayment ? { full_payment_collection: 'dealer' as const } : {}),
            dealer_payment: toDealerPaymentPayload(routing.dealerPayment as DealerCollectedDeposit),
        };
    }
    if (isPaidInFull && routing.fullPaymentCollection === 'elemis') {
        return { full_payment_collection: 'elemis' };
    }
    if (isPaidInFull) {
        throw new Error(MALFORMED_FULL_PAYMENT_ROUTING);
    }
    // A stale full-payment axis on a scheduled method is dropped: absence there means
    // "Elemis collects the deposit", today's behaviour, and quote-service 422s the pairing.
    return undefined;
};

// Only absence maps to absence. A truthiness check would drop a falsey malformed
// value (`0`, `false`) and let the predicate below accept the routing.
const optionalInvoiceNumber = (value: unknown): { invoiceNumber?: unknown } =>
    value === undefined || value === null ? {} : { invoiceNumber: value };

// Wire JSON is untrusted and `isValidPaymentSelection` never checks routing: an
// unrepresentable stored routing throws rather than hydrating as legacy no-routing.
const fromCollectionRoutingPayload = (
    payload: PaymentSelectionPayload['collection_routing'],
    methodKey: string,
): CollectionRouting | undefined => {
    if (payload === undefined || payload === null) {
        return undefined;
    }
    const isPaidInFull = methodKey === PAID_IN_FULL_METHOD_KEY;
    const isDealerDeposit = !isPaidInFull && payload.deposit_collection === 'dealer';
    const isDealerFullPayment = isPaidInFull && payload.full_payment_collection === 'dealer';
    if (isDealerDeposit || isDealerFullPayment) {
        const wire = payload.dealer_payment;
        const dealerPayment = wire
            ? {
                  amountMinor: wire.amount_minor,
                  reference: wire.reference,
                  ...optionalInvoiceNumber(wire.invoice_number),
                  collectedAt: wire.collected_at,
              }
            : undefined;
        if (!isValidDealerPayment(dealerPayment)) {
            if (isDealerFullPayment) {
                throw new Error(MALFORMED_FULL_PAYMENT_ROUTING);
            }
            return undefined;
        }
        return {
            ...(isDealerDeposit ? { depositCollection: 'dealer' as const } : {}),
            ...(isDealerFullPayment ? { fullPaymentCollection: 'dealer' as const } : {}),
            dealerPayment,
        };
    }
    if (isPaidInFull && payload.full_payment_collection === 'elemis') {
        return { fullPaymentCollection: 'elemis' };
    }
    return undefined;
};

// #767: the wire check for `schedule_anchor_date` is `isDateKey`
// (`utils/dateKey.ts`). Both directions guard the CALENDAR VALIDITY, not
// merely the type or the shape, for the same reason #728's mapping uses
// `Number.isInteger` rather than `typeof === 'number'`: the value arrives off
// a redux-persist rehydration or off untrusted wire JSON, so its runtime
// shape is not type-guaranteed, and `isValidPaymentSelection` deliberately
// leaves the optional field unchecked.
//
// A local `/^\d{4}-\d{2}-\d{2}$/` used to live here. It admitted `2026-99-99`
// and `2026-02-30`, which are shaped like dates and are not dates — and the
// only consumer of this field (`resolveAppliedDeposit`) decides whether to
// print a money figure by comparing it to `schedule[0].date`. One predicate,
// owned by the module that owns date-key semantics, is now used at both the
// wire boundary and inside that guard.

// quote-service round-trips this document verbatim, like `payment_selection`

// Only serialise a payment selection that passes the tightened hydrate
// guard. A malformed/legacy hydrated value would otherwise produce a
// `payment_selection` with `snapshot: undefined` on the wire; omit it
// entirely instead so the backend never receives a half-formed selection.
export const toPaymentSelectionPayload = (
    selection: PaymentSelection | undefined,
): PaymentSelectionPayload | undefined => {
    if (!isValidPaymentSelection(selection)) {
        return undefined;
    }
    const payload: PaymentSelectionPayload = {
        method_key: selection.methodKey,
        params: selection.params,
        snapshot: selection.snapshot,
    };
    // #728: emit collection_lead_months ONLY when it is a whole number. A
    // legacy selection (predating #728) or one on a method that does not
    // honour the lead carries `undefined` here, and omitting the wire key
    // entirely — rather than sending `collection_lead_months: undefined` or
    // `null` — keeps the payload byte-identical to today for both cases.
    //
    // The guard is `Number.isInteger`, not a bare `typeof === 'number'`:
    // `NaN` and `Infinity` are both `typeof 'number'` yet `JSON.stringify`
    // encodes both as `null`, which is precisely the wire shape the contract
    // above rules out. The `typeof` half exists only to narrow `number |
    // undefined` for the assignment; `Number.isInteger` is what enforces
    // "whole months". This matters because `collectionLeadMonths` reaches
    // here off the redux-persist-rehydrated `quote` slice, and
    // `isValidPaymentSelection` deliberately leaves the optional field
    // unchecked — so its runtime shape is not type-guaranteed on that path.
    const leadMonths = selection.collectionLeadMonths;
    if (typeof leadMonths === 'number' && Number.isInteger(leadMonths)) {
        payload.collection_lead_months = leadMonths;
    }
    // #767: emit schedule_anchor_date ONLY when it is a valid `YYYY-MM-DD`
    // calendar key. A malformed anchor (e.g. surviving a hand-edited or
    // corrupted persisted state) is dropped, not carried — which lands the
    // selection in the guard's silent no-anchor fallback, exactly where an
    // unverifiable anchor belongs.
    const anchor = selection.scheduleAnchorDate;
    if (isDateKey(anchor)) {
        payload.schedule_anchor_date = anchor;
    }
    // Deposit axis: emitted only when dealer-collected (an `elemis` choice stays
    // unset). Full-payment axis: BOTH values are emitted.
    const collectionRouting = toCollectionRoutingPayload(
        selection.collectionRouting,
        selection.methodKey,
    );
    if (collectionRouting !== undefined) {
        payload.collection_routing = collectionRouting;
    }
    return payload;
};

// Maps the wire `payment_selection` (snake_case method_key, camelCase
// numPayments — see StoreQuoteRequest::rules()) back into the in-app
// `PaymentSelection` shape, guarded by isValidPaymentSelection so a
// missing/malformed field never satisfies the payment-step gate or gets
// dereferenced downstream (D6).
export const fromPaymentSelectionPayload = (
    payload: PaymentSelectionPayload | null | undefined,
): PaymentSelection | undefined => {
    if (!payload) {
        return undefined;
    }
    const wireLeadMonths = payload.collection_lead_months;
    const wireAnchor = payload.schedule_anchor_date;
    const collectionRouting = fromCollectionRoutingPayload(
        payload.collection_routing,
        payload.method_key,
    );
    const candidate: PaymentSelection = {
        methodKey: payload.method_key,
        params: payload.params,
        snapshot: payload.snapshot,
        // #728: mapped back only when present on the wire as a whole number.
        // The conditional spread mirrors the one in `handleConfirm`
        // (PaymentStep) and exists for the same reason: a plain
        // `collectionLeadMonths: payload.collection_lead_months` would create
        // an own property holding `undefined` when the wire key is absent
        // (every pre-#728 quote, and every non-item-based selection), whereas
        // the contract says such a selection omits the field entirely.
        // Hydrating a legacy quote must produce an object shaped exactly like
        // the one that was saved.
        //
        // `Number.isInteger` rather than a bare `typeof === 'number'`, to
        // match the write path above: it rejects a non-integer the contract
        // calls whole months, and it keeps both directions of the round trip
        // agreeing on what counts as a lead worth carrying.
        ...(typeof wireLeadMonths === 'number' && Number.isInteger(wireLeadMonths)
            ? { collectionLeadMonths: wireLeadMonths }
            : {}),
        // #767: mapped back only when present on the wire as a valid
        // `YYYY-MM-DD` calendar key — mirrors the write path's check for the
        // same reason: the wire value is untrusted JSON, not a type-checked
        // `DateKey`. A malformed or absent value omits the field entirely,
        // landing the hydrated selection in the guard's silent fallback.
        ...(isDateKey(wireAnchor) ? { scheduleAnchorDate: wireAnchor } : {}),
        // Mapped back only when the wire carries a well-formed dealer-collected
        // routing, for the same untrusted-JSON reason as the two fields above.
        ...(collectionRouting ? { collectionRouting } : {}),
    };
    return isValidPaymentSelection(candidate) ? candidate : undefined;
};
